"use client";

import { useEffect, useRef, useState } from "react";
import {
  clampPosition,
  snapToEdge,
  reflowPosition,
  readSavedPosition,
  writeSavedPosition,
  clearSavedPosition,
  defaultPosition,
  type SavedPosition,
} from "./feedback-position-utils";
import {
  parseBroadcastsResponse,
  readDismissed,
  writeDismissed,
  readDismissCounter,
  writeDismissCounter,
  filterVisible,
  colourForSeverity,
  cacheGet,
  cacheSet,
  MAX_DISMISSALS_BEFORE_SUPPRESS,
  type Broadcast,
} from "./feedback-broadcast-utils";
import { redactText, redactUrl } from "./feedback-redact";

/**
 * 180D Feedback Widget — reference copy.
 *
 * Copy this file into a consuming app's `components/` directory.
 * Mount once in the root layout. Zero deps beyond React.
 *
 * Env vars expected by the consuming app:
 *   NEXT_PUBLIC_FEEDBACK_URL       — https://feedback.oneighty.cloud/ingest
 *   NEXT_PUBLIC_FEEDBACK_TOKEN     — shared ingest secret (spam-only blast radius)
 *   NEXT_PUBLIC_FEEDBACK_APP_SLUG  — this app's slug (e.g. "psa", "brightmesh")
 */

type Category = "bug" | "feature" | "idea" | "love" | "hate";
type Severity = "p1" | "normal";

type Props = {
  appSlug: string;
  ingestUrl: string;
  ingestToken: string;
  userEmail?: string;
  userWorkspaceId?: string;
};

const PSA_BLUE = "#1d4ed8";
const PSA_BLUE_HOVER = "#1e40af";

export function FeedbackWidget({
  appSlug,
  ingestUrl,
  ingestToken,
  userEmail,
  userWorkspaceId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>("bug");
  const [severity, setSeverity] = useState<Severity>("normal");
  const [message, setMessage] = useState("");
  // Structured Bug fields (shown only when category === "bug").
  // Triage was getting "it broke" with no context; these three prompts force
  // the reporter to surface tried/happened/expected so engineering can act.
  const [bugTried, setBugTried] = useState("");
  const [bugHappened, setBugHappened] = useState("");
  const [bugExpected, setBugExpected] = useState("");
  // Structured Feature request fields (shown only when category === "feature").
  // Same idea: force the reporter to articulate the problem, not just the
  // solution, so engineering can choose between several implementations.
  const [featureWhat, setFeatureWhat] = useState("");
  const [featureWhy, setFeatureWhy] = useState("");
  const [featureUrgency, setFeatureUrgency] = useState<"nice" | "soon" | "blocking">("nice");
  const [email, setEmail] = useState(userEmail ?? "");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Phase 17 Plan-2: active broadcasts. Polled from the central Worker
  // on widget mount with a 60s client-side cache (matches the back-end's
  // edge cache TTL). Banners render inside the panel above the form.
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  const [dismissCounter, setDismissCounter] = useState(0);

  // Phase 14 Plan-1: draggable icon with remembered position.
  // Initialised null so the first render uses the legacy bottom-right
  // CSS (right: 20, bottom: 20) and there's no visible jump on hydration.
  // Hydration effect below resolves the saved position (or default).
  const ICON_SIZE = { width: 48, height: 48 };
  const [position, setPosition] = useState<SavedPosition | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
    moved: boolean;
    rafId: number | null;
    nextX: number;
    nextY: number;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Hydrate position from localStorage on first mount. Falls back to the
  // default corner if storage is empty or unavailable.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storage = (() => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    })();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const saved = readSavedPosition(storage);
    setPosition(saved ? reflowPosition(saved, ICON_SIZE, viewport) : defaultPosition(ICON_SIZE, viewport));
  }, []);

  // On window resize, reflow saved position back into the new viewport
  // so the icon doesn't end up off-screen after the user shrinks the window.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setPosition((prev) => {
        if (!prev) return prev;
        return reflowPosition(prev, ICON_SIZE, { width: window.innerWidth, height: window.innerHeight });
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the context menu when the user clicks anywhere else.
  useEffect(() => {
    if (!contextMenu) return;
    const onClick = () => setContextMenu(null);
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [contextMenu]);

  // Phase 17 Plan-2: fetch active broadcasts on mount (and once an hour
  // while the page stays open — broadcasts have short windows so a stale
  // view past the cache TTL would miss the start of a maintenance window).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sessionStorageSafe = (() => {
      try { return window.sessionStorage; } catch { return null; }
    })();
    setDismissedIds(readDismissed(sessionStorageSafe));
    setDismissCounter(readDismissCounter(sessionStorageSafe));

    const broadcastsUrl = ingestUrl.replace(/\/ingest$/, "") + "/broadcasts/active";
    const cacheKey = `broadcasts:${appSlug}`;

    async function fetchBroadcasts() {
      const cached = cacheGet<Broadcast[]>(cacheKey);
      if (cached) {
        setBroadcasts(cached);
        return;
      }
      try {
        const res = await fetch(`${broadcastsUrl}?app_slug=${encodeURIComponent(appSlug)}`, {
          headers: { "x-feedback-secret": ingestToken },
        });
        if (!res.ok) return;
        const data = await res.json();
        const parsed = parseBroadcastsResponse(data);
        cacheSet(cacheKey, parsed);
        setBroadcasts(parsed);
      } catch {
        // Network failure is non-fatal — the widget still works without
        // broadcasts. Silent so we don't spam console on every flaky network.
      }
    }
    fetchBroadcasts();
    // Hourly refresh while the tab stays open
    const intervalId = window.setInterval(fetchBroadcasts, 60 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [appSlug, ingestToken, ingestUrl]);

  function dismissBroadcast(id: number) {
    const sessionStorageSafe = (() => {
      try { return typeof window !== "undefined" ? window.sessionStorage : null; } catch { return null; }
    })();
    const next = new Set(dismissedIds);
    next.add(id);
    setDismissedIds(next);
    writeDismissed(sessionStorageSafe, next);
    const newCount = dismissCounter + 1;
    setDismissCounter(newCount);
    writeDismissCounter(sessionStorageSafe, newCount);
  }

  const visibleBroadcasts = filterVisible(broadcasts, dismissedIds, dismissCounter);

  function handlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (e.button !== 0) return; // left-click only; right-click goes to context menu
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
      rafId: null,
      nextX: rect.left,
      nextY: rect.top,
    };
  }

  function handlePointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const ds = dragStateRef.current;
    if (!ds || ds.pointerId !== e.pointerId) return;
    // Only count as drag once the user moves more than a small threshold
    // — otherwise a regular click would feel like a drag and never open.
    const dx = Math.abs(e.clientX - (ds.nextX + ds.offsetX));
    const dy = Math.abs(e.clientY - (ds.nextY + ds.offsetY));
    if (!ds.moved && dx < 4 && dy < 4) return;
    ds.moved = true;
    ds.nextX = e.clientX - ds.offsetX;
    ds.nextY = e.clientY - ds.offsetY;
    if (ds.rafId == null) {
      ds.rafId = window.requestAnimationFrame(() => {
        const s = dragStateRef.current;
        if (!s) return;
        s.rafId = null;
        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const clamped = clampPosition({ x: s.nextX, y: s.nextY }, ICON_SIZE, viewport);
        setPosition({ ...clamped, edge: "free", v: 1 });
      });
    }
  }

  function handlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const ds = dragStateRef.current;
    if (!ds || ds.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    const moved = ds.moved;
    if (ds.rafId != null) window.cancelAnimationFrame(ds.rafId);
    dragStateRef.current = null;

    if (!moved) {
      // Treated as a normal click → open the panel.
      setOpen(true);
      return;
    }

    // Snap to edge if close, persist either way.
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const snapped = snapToEdge({ x: ds.nextX, y: ds.nextY }, ICON_SIZE, viewport);
    setPosition(snapped);
    const storage = (() => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    })();
    writeSavedPosition(storage, snapped);
  }

  function handleContextMenu(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }

  function resetPosition() {
    setContextMenu(null);
    if (typeof window === "undefined") return;
    const storage = (() => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    })();
    clearSavedPosition(storage);
    setPosition(defaultPosition(ICON_SIZE, { width: window.innerWidth, height: window.innerHeight }));
  }

  useEffect(() => {
    if (status !== "ok") return;
    const t = setTimeout(() => {
      setOpen(false);
      setMessage("");
      setBugTried("");
      setBugHappened("");
      setBugExpected("");
      setFeatureWhat("");
      setFeatureWhy("");
      setFeatureUrgency("nice");
      setStatus("idle");
    }, 1800);
    return () => clearTimeout(t);
  }, [status]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusables = dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusables?.[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab" || !focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Compose the body that gets sent. For Bug: three labelled sections so
  // triage doesn't have to guess. For everything else: the plain message.
  // Auto-captured context (route, viewport, screen, timestamp) is appended
  // to every submission so we know where + when it happened without asking.
  function composeMessage(): string {
    const parts: string[] = [];
    if (category === "bug") {
      const t = bugTried.trim();
      const h = bugHappened.trim();
      const x = bugExpected.trim();
      if (t) parts.push(`**What I tried**\n${t}`);
      if (h) parts.push(`**What happened**\n${h}`);
      if (x) parts.push(`**What I expected**\n${x}`);
      if (message.trim()) parts.push(`**More**\n${message.trim()}`);
    } else if (category === "feature") {
      const w = featureWhat.trim();
      const y = featureWhy.trim();
      if (w) parts.push(`**What I want**\n${w}`);
      if (y) parts.push(`**Why it matters**\n${y}`);
      const urgencyLabel =
        featureUrgency === "blocking"
          ? "Blocking — can't ship without it"
          : featureUrgency === "soon"
          ? "Needed soon"
          : "Nice to have";
      parts.push(`**Urgency:** ${urgencyLabel}`);
      if (message.trim()) parts.push(`**More**\n${message.trim()}`);
    } else if (message.trim()) {
      parts.push(message.trim());
    }
    if (typeof window !== "undefined") {
      const ctx: string[] = [];
      // Redact the route before it leaves the browser: on authenticated SEER
      // routes this is `/children/<child-uuid>/…` — a tenant identifier on every
      // report (BL-001 egress class). redactUrl strips IDs and drops query content.
      ctx.push(`- Page route: \`${redactUrl(`${window.location.pathname}${window.location.search}`)}\``);
      if (window.innerWidth) ctx.push(`- Viewport: ${window.innerWidth}x${window.innerHeight}`);
      if (window.screen?.width) ctx.push(`- Screen: ${window.screen.width}x${window.screen.height}`);
      ctx.push(`- Time: ${new Date().toISOString()}`);
      parts.push(`**Context (auto-captured)**\n${ctx.join("\n")}`);
    }
    return parts.join("\n\n");
  }

  function isReady(): boolean {
    if (category === "bug") {
      return Boolean(bugTried.trim() || bugHappened.trim() || bugExpected.trim() || message.trim());
    }
    if (category === "feature") {
      return Boolean(featureWhat.trim() || featureWhy.trim() || message.trim());
    }
    return Boolean(message.trim());
  }

  async function submit() {
    if (!isReady() || sending) return;
    setSending(true);
    setStatus("idle");
    setErrorMsg("");

    try {
      const res = await fetch(ingestUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-feedback-secret": ingestToken,
        },
        body: JSON.stringify({
          app_slug: appSlug,
          category,
          severity: category === "bug" ? severity : "normal",
          // Redact the whole composed message (defence-in-depth: a reporter may
          // type a Medicare/NDIS number or paste a child UUID into the free text)
          // and the captured href, before they cross the tenant boundary to the
          // external Feedback worker. Parity with the BL-001-hardened crash client.
          message: redactText(composeMessage()),
          url: typeof window !== "undefined" ? redactUrl(window.location.href) : undefined,
          user_email: email.trim() || undefined,
          user_workspace_id: userWorkspaceId,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      setStatus("ok");
    } catch (e) {
      setStatus("error");
      setErrorMsg(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        // onClick is intentionally absent — open vs drag is decided in
        // handlePointerUp based on whether the pointer actually moved.
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onContextMenu={handleContextMenu}
        aria-label="Send feedback"
        title="Send feedback (drag to move, right-click for options)"
        style={{
          position: "fixed",
          // When position state is null (pre-hydration / SSR), fall back
          // to the legacy CSS so first paint matches the long-standing
          // bottom-right spot. After hydration, state-driven left/top
          // takes over and right/bottom are removed.
          ...(position
            ? { left: position.x, top: position.y }
            : { right: 20, bottom: 20 }),
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "none",
          background: PSA_BLUE,
          color: "white",
          fontSize: 20,
          cursor: dragStateRef.current?.moved ? "grabbing" : "grab",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9998,
          transition: dragStateRef.current ? "none" : "background 0.15s, left 0.2s, top 0.2s",
          touchAction: "none", // PointerEvents on touch require this to suppress scroll
          userSelect: "none",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = PSA_BLUE_HOVER)}
        onMouseLeave={(e) => (e.currentTarget.style.background = PSA_BLUE)}
      >
        ?
        {/*
          Phase 17 Plan-3: closed-state notification dot.
          Shown when there's a non-info active broadcast that the user
          hasn't dismissed. Colour matches the highest-severity active
          broadcast (red trumps amber). Info-severity broadcasts get NO
          dot — they're informational only, not "you should look at this
          before clicking" urgent. Tap the icon opens the panel, and the
          banner inside takes over.
        */}
        {(() => {
          if (open) return null;
          const urgent = visibleBroadcasts.filter((b) => b.severity !== "info");
          if (urgent.length === 0) return null;
          const isCritical = urgent.some((b) => b.severity === "critical");
          const dotColour = isCritical ? "#dc2626" : "#f59e0b"; // red / amber
          return (
            <span
              aria-label={isCritical ? "Critical notice" : "Notice"}
              role="status"
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: dotColour,
                border: "2px solid white",
                pointerEvents: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
            />
          );
        })()}
      </button>

      {contextMenu ? (
        <div
          role="menu"
          aria-label="Widget options"
          style={{
            position: "fixed",
            left: contextMenu.x,
            top: contextMenu.y,
            background: "white",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: 4,
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: 13,
            zIndex: 10000,
            minWidth: 160,
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={resetPosition}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              border: "none",
              background: "transparent",
              padding: "6px 12px",
              cursor: "pointer",
              color: "inherit",
              fontSize: "inherit",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            Reset position
          </button>
        </div>
      ) : null}

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            ref={dialogRef}
            style={{
              width: "min(480px, 92vw)",
              background: "var(--feedback-bg, #ffffff)",
              color: "var(--feedback-fg, #111827)",
              borderRadius: 12,
              padding: 24,
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Feedback</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "inherit" }}
              >
                x
              </button>
            </div>

            {visibleBroadcasts.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                {visibleBroadcasts.map((b) => {
                  const c = colourForSeverity(b.severity);
                  const dismissible = b.severity !== "critical";
                  return (
                    <div
                      key={b.id}
                      role="status"
                      aria-live="polite"
                      style={{
                        background: c.bg,
                        color: c.fg,
                        border: `1px solid ${c.border}`,
                        borderRadius: 6,
                        padding: "10px 12px",
                        marginBottom: 8,
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>{b.title}</div>
                          <div style={{ whiteSpace: "pre-wrap" }}>{b.body}</div>
                        </div>
                        {dismissible ? (
                          <button
                            type="button"
                            onClick={() => dismissBroadcast(b.id)}
                            aria-label="Dismiss"
                            style={{
                              border: "none",
                              background: "transparent",
                              color: c.fg,
                              cursor: "pointer",
                              fontSize: 18,
                              lineHeight: 1,
                              padding: "0 4px",
                            }}
                            title="Dismiss for this session"
                          >
                            x
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {dismissCounter >= MAX_DISMISSALS_BEFORE_SUPPRESS - 1 && visibleBroadcasts.some(b => b.severity !== "critical") ? (
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    Dismissing more in this session will hide non-critical notices until you reload.
                  </div>
                ) : null}
              </div>
            ) : null}


            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {(["bug", "feature", "idea", "love", "hate"] as Category[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    borderRadius: 6,
                    border: `1px solid ${category === c ? PSA_BLUE : "rgba(0,0,0,0.15)"}`,
                    background: category === c ? PSA_BLUE : "transparent",
                    color: category === c ? "white" : "inherit",
                    fontSize: 12,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {c === "feature" ? "Feature" : c}
                </button>
              ))}
            </div>

            {category === "bug" ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {(["normal", "p1"] as Severity[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSeverity(s)}
                    style={{
                      flex: 1,
                      padding: "6px 4px",
                      borderRadius: 6,
                      border: `1px solid ${severity === s ? PSA_BLUE : "rgba(0,0,0,0.15)"}`,
                      background: severity === s ? PSA_BLUE : "transparent",
                      color: severity === s ? "white" : "inherit",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    {s === "p1" ? "P1 — blocking" : "Normal"}
                  </button>
                ))}
              </div>
            ) : null}

            {category === "bug" ? (
              <>
                {([
                  { value: bugTried, set: setBugTried, label: "What did you try to do?", rows: 2 },
                  { value: bugHappened, set: setBugHappened, label: "What happened?", rows: 2 },
                  { value: bugExpected, set: setBugExpected, label: "What did you expect to happen?", rows: 2 },
                ] as const).map((field) => (
                  <label key={field.label} style={{ display: "block", marginBottom: 10 }}>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.85 }}>
                      {field.label}
                    </span>
                    <textarea
                      value={field.value}
                      onChange={(e) => field.set(e.target.value)}
                      rows={field.rows}
                      maxLength={2000}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: "var(--feedback-input-bg, #f9fafb)",
                        color: "inherit",
                        fontSize: 14,
                        fontFamily: "inherit",
                        resize: "vertical",
                      }}
                    />
                  </label>
                ))}
              </>
            ) : category === "feature" ? (
              <>
                {([
                  { value: featureWhat, set: setFeatureWhat, label: "What do you want?", rows: 2 },
                  { value: featureWhy, set: setFeatureWhy, label: "Why? (what problem does it solve?)", rows: 2 },
                ] as const).map((field) => (
                  <label key={field.label} style={{ display: "block", marginBottom: 10 }}>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.85 }}>
                      {field.label}
                    </span>
                    <textarea
                      value={field.value}
                      onChange={(e) => field.set(e.target.value)}
                      rows={field.rows}
                      maxLength={2000}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: "var(--feedback-input-bg, #f9fafb)",
                        color: "inherit",
                        fontSize: 14,
                        fontFamily: "inherit",
                        resize: "vertical",
                      }}
                    />
                  </label>
                ))}
                <div style={{ display: "block", marginBottom: 12 }}>
                  <span style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: 0.85 }}>
                    Urgency
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {([
                      { v: "nice", l: "Nice to have" },
                      { v: "soon", l: "Needed soon" },
                      { v: "blocking", l: "Blocking" },
                    ] as const).map((u) => (
                      <button
                        key={u.v}
                        type="button"
                        onClick={() => setFeatureUrgency(u.v)}
                        style={{
                          flex: 1,
                          padding: "6px 4px",
                          borderRadius: 6,
                          border: `1px solid ${featureUrgency === u.v ? PSA_BLUE : "rgba(0,0,0,0.15)"}`,
                          background: featureUrgency === u.v ? PSA_BLUE : "transparent",
                          color: featureUrgency === u.v ? "white" : "inherit",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        {u.l}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                rows={5}
                maxLength={8000}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  borderRadius: 6,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "var(--feedback-input-bg, #f9fafb)",
                  color: "inherit",
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical",
                  marginBottom: 12,
                }}
              />
            )}

            <p style={{ fontSize: 12, opacity: 0.7, margin: "0 0 12px 0" }}>
              Please don&apos;t paste passwords or sensitive customer data.
            </p>

            {!userEmail ? (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional, so we can reply)"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 10,
                  borderRadius: 6,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "var(--feedback-input-bg, #f9fafb)",
                  color: "inherit",
                  fontSize: 14,
                  marginBottom: 12,
                }}
              />
            ) : null}

            {status === "error" ? (
              <p style={{ color: "#dc2626", fontSize: 13, margin: "0 0 12px 0" }}>
                Couldn&apos;t send — {errorMsg}. Try again.
              </p>
            ) : null}

            {status === "ok" ? (
              <p style={{ color: "#16a34a", fontSize: 13, margin: "0 0 12px 0" }}>
                Thanks — we&apos;ve got it.
              </p>
            ) : null}

            <button
              type="button"
              onClick={submit}
              disabled={sending || !isReady() || status === "ok"}
              style={{
                width: "100%",
                padding: "10px 16px",
                borderRadius: 6,
                border: "none",
                background: sending || !isReady() ? "rgba(0,0,0,0.2)" : PSA_BLUE,
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                cursor: sending || !isReady() ? "not-allowed" : "pointer",
              }}
            >
              {sending ? "Sending..." : status === "ok" ? "Sent" : "Send"}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
